import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import PublicIcon from '@mui/icons-material/Public';

export type TzOption = {
    value: string;       // IANA id, e.g. "Europe/Warsaw"
    label: string;       // "(UTC+02:00) Europe/Warsaw"
    group: string;       // continent e.g. "Europe"
    offsetMinutes: number;
};

export interface TimeZoneSelectProps {
    value?: string | null;
    onChange: (value: string | null, option: TzOption | null) => void;
    label?: string;
    placeholder?: string;
    fullWidth?: boolean;
    size?: 'small' | 'medium';
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    /** Strefy przypięte u góry listy (w kolejności) */
    pinned?: string[];
    /** Wyłącz grupowanie po kontynencie */
    disableGrouping?: boolean;
    /** Dodatkowe style MUI */
    sx?: any;
}

const FALLBACK_TIMEZONES = [
    'UTC',
    'Europe/Warsaw', 'Europe/London', 'Europe/Berlin', 'Europe/Istanbul',
    'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
    'America/Sao_Paulo',
    'Asia/Dubai', 'Asia/Jerusalem', 'Asia/Karachi', 'Asia/Dhaka',
    'Asia/Bangkok', 'Asia/Shanghai', 'Asia/Tokyo',
    'Australia/Sydney', 'Pacific/Auckland',
];

const parseOffsetMinutes = (tzName: string) => {
    // tzName like "GMT+02:00"
    const m = tzName.match(/GMT([+-])(\d{2}):?(\d{2})?/i);
    if (!m) return 0;
    const sign = m[1] === '-' ? -1 : 1;
    const hh = parseInt(m[2] || '0', 10);
    const mm = parseInt(m[3] || '0', 10);
    return sign * (hh * 60 + mm);
};

const buildTimeZoneOptions = (): TzOption[] => {
    const all: string[] =
        typeof (Intl as any).supportedValuesOf === 'function'
            ? (Intl as any).supportedValuesOf('timeZone')
            : FALLBACK_TIMEZONES;

    const now = new Date();
    const out: TzOption[] = [];

    for (const value of all) {
        try {
            const tzName =
                new Intl.DateTimeFormat('en-US', {
                    timeZone: value,
                    timeZoneName: 'longOffset',
                    hour: '2-digit',
                })
                    .formatToParts(now)
                    .find(p => p.type === 'timeZoneName')?.value || 'GMT+00:00';

            const offsetMin = parseOffsetMinutes(tzName);
            const sign = offsetMin >= 0 ? '+' : '-';
            const abs = Math.abs(offsetMin);
            const hh = String(Math.floor(abs / 60)).padStart(2, '0');
            const mm = String(abs % 60).padStart(2, '0');
            const label = `(UTC${sign}${hh}:${mm}) ${value}`;
            const group = value.includes('/') ? value.split('/')[0] : 'Other';

            out.push({ value, label, group, offsetMinutes: offsetMin });
        } catch {
            // Some rare zones can throw RangeError on certain environments—skip them
        }
    }

    return out.sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.label.localeCompare(b.label));
};

export const TimeZoneSelect: React.FC<TimeZoneSelectProps> = ({
    value,
    onChange,
    label = 'Time Zone',
    placeholder = 'Search time zone (e.g. Europe/Warsaw)',
    fullWidth = true,
    size = 'medium',
    disabled,
    required,
    error,
    helperText,
    pinned = ['Europe/Warsaw', 'UTC'],
    disableGrouping = false,
    sx,
}) => {
    const options = React.useMemo(buildTimeZoneOptions, []);
    // Build pinned list first (preserve order), then the rest without duplicates
    const pinnedOpts = React.useMemo(
        () => pinned.map(p => options.find(o => o.value === p)).filter(Boolean) as TzOption[],
        [pinned, options]
    );
    const nonPinned = React.useMemo(
        () => options.filter(o => !pinnedOpts.some(p => p.value === o.value)),
        [options, pinnedOpts]
    );
    const finalOptions = React.useMemo(() => [...pinnedOpts, ...nonPinned], [pinnedOpts, nonPinned]);

    const selected = React.useMemo(
        () => finalOptions.find(o => o.value === (value ?? '')) || null,
        [finalOptions, value]
    );

    return (
        <Autocomplete<TzOption, false, false, false>
            options={finalOptions}
            value={selected}
            onChange={(_, opt) => onChange(opt?.value ?? null, opt ?? null)}
            groupBy={disableGrouping ? undefined : (opt) => opt.group}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            sx={sx}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    fullWidth={fullWidth}
                    size={size}
                    disabled={disabled}
                    required={required}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <PublicIcon fontSize="small" sx={{ color: 'info.main' }} />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
};
